import type { AnchorHTMLAttributes, ReactNode } from 'react'

import { cn } from '@/lib/cn'

import type { DayStatus } from './api'
import { STATUS } from './meta'
import { follow, href } from './router'

/** A link to one of the page's own places. */
export function Link({
  to,
  children,
  className,
  ...props
}: { to: string; children: ReactNode } & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'>) {
  return (
    <a href={href(to)} onClick={follow(to)} className={className} {...props}>
      {children}
    </a>
  )
}

/** A status's light; a live one breathes. */
export function Dot({
  status,
  pulse = false,
  className,
}: {
  status: DayStatus
  pulse?: boolean
  className?: string
}) {
  const look = STATUS[status]
  return (
    <span className={cn('relative inline-flex size-2.5 shrink-0', className)} aria-hidden>
      {pulse && <span className={cn('animate-ping-soft absolute inset-0 rounded-full', look.bg)} />}
      <span className={cn('relative size-2.5 rounded-full', look.bg)} />
    </span>
  )
}

export function Pill({
  status,
  children,
  className,
}: {
  status: DayStatus
  children: ReactNode
  className?: string
}) {
  const look = STATUS[status]
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[12px] font-semibold whitespace-nowrap ring-1',
        look.text,
        look.ring,
        'bg-white/[0.03]',
        className,
      )}
    >
      {children}
    </span>
  )
}

export function SectionTitle({ children, aside }: { children: ReactNode; aside?: ReactNode }) {
  return (
    <div className="mb-4 flex items-end justify-between gap-4">
      <h2 className="font-display text-[15px] font-semibold tracking-[-0.01em] text-white">
        {children}
      </h2>
      {aside}
    </div>
  )
}
